import { useQuery } from '@tanstack/react-query';
import { clientForSchema, supabaseHarvest } from '@/lib/supabase';
import { harvestEndpointTypes, type EntityDefinition, type ProviderDefinition } from '@/config/providers';

type SchemaClient = ReturnType<typeof clientForSchema>;

/** The later of two ISO timestamps; tolerates nulls (a missing side loses). */
function maxIso(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) >= new Date(b) ? a : b;
}

/**
 * endpoint_type → latest `collected_at` for a provider, read from the
 * pre-aggregated harvest.v_manifest_summary view (one row per
 * provider/sport/endpoint_type). Optionally narrowed to a set of endpoint
 * types. The view is tiny (a few dozen rows), so one fetch covers every card.
 */
async function fetchManifestLastCollected(
  providerKey: string,
  endpointTypes?: string[],
): Promise<Map<string, string>> {
  let q = supabaseHarvest
    .from('v_manifest_summary')
    .select('endpoint_type, last_collected_at')
    .eq('provider', providerKey);
  if (endpointTypes?.length) q = q.in('endpoint_type', endpointTypes);
  const { data, error } = await q;
  if (error) throw error;
  const map = new Map<string, string>();
  for (const r of (data ?? []) as { endpoint_type: string; last_collected_at: string | null }[]) {
    if (!r.last_collected_at) continue;
    map.set(r.endpoint_type, maxIso(map.get(r.endpoint_type) ?? null, r.last_collected_at)!);
  }
  return map;
}

/**
 * Accurate row counts for a set of tables in a v2 schema, via the
 * `harvest.fn_entity_row_counts` RPC (returns pg_stat n_live_tup). One round-trip
 * for the whole grid; immune to the RLS-qual planner-estimate that made
 * `count=estimated` read ~1/3 of reality. Returns an empty map on error so the
 * caller can render "—" rather than a wrong number.
 */
async function fetchEntityRowCounts(schema: string, tables: string[]): Promise<Map<string, number>> {
  const result = new Map<string, number>();
  const { data, error } = await supabaseHarvest.rpc('fn_entity_row_counts', {
    p_schema: schema,
    p_tables: tables,
  });
  if (error) return result;
  for (const r of (data ?? []) as { table_name: string; row_count: number | string }[]) {
    result.set(r.table_name, Number(r.row_count));
  }
  return result;
}

/** Latest harvest across all of an entity's feeding endpoint type(s). */
function lastHarvestedFor(entity: EntityDefinition, map: Map<string, string>): string | null {
  let latest: string | null = null;
  for (const et of harvestEndpointTypes(entity)) latest = maxIso(latest, map.get(et) ?? null);
  return latest;
}

/**
 * Most recent typed-table write — `max(updated_at)`.
 *
 * MUST use the DEFAULT descending null ordering (NULLS FIRST) so PostgreSQL
 * serves this as an instant **Index Only Scan Backward** on
 * `idx_<table>_updated_at` (every V2 entity table has one), stopping at the
 * first row. The previous `nullsFirst: false` emitted `ORDER BY updated_at DESC
 * NULLS LAST`, which the default `(updated_at ASC NULLS LAST)` index can't serve
 * in reverse — PostgreSQL fell back to a full top-N heapsort over the whole
 * table (≈300ms on match_person_stats @ 880k rows), which blew the read-only
 * role's statement_timeout on the largest tables. The error was caught upstream
 * and rendered as "Synced —", making regularly-synced entities look unsynced.
 *
 * `not('updated_at','is',null)` keeps the result the latest NON-null write
 * without bringing NULLS LAST back: under DESC, NULLS FIRST would otherwise
 * surface a null row first, so we exclude nulls in the WHERE clause instead
 * (which the index scan still satisfies — verified: Index Only Scan Backward,
 * ~0.1ms).
 */
async function fetchLastSynced(client: SchemaClient, table: string): Promise<string | null> {
  const { data, error } = await client
    .from(table)
    .select('updated_at')
    .not('updated_at', 'is', null)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  return (data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? null;
}

export interface EntityQueryParams {
  sport?: string;
  search?: string;
  limit?: number;
  /**
   * Server-side sort column. The grid is capped at `limit` rows, so sorting
   * must happen in PostgREST (`.order()`) to order the whole table rather than
   * just the returned page. When unset, falls back to `entity.orderBy`.
   */
  sortColumn?: string;
  sortAscending?: boolean;
}

/**
 * Strip characters that have meaning in a PostgREST `or()` expression so a
 * value coming from a free-text search box cannot inject extra filter
 * predicates. Admin-only / behind RLS so the realistic blast radius is
 * narrow, but keeping the gate is cheap and makes the intent explicit.
 *
 *   %  — wildcard in ilike (allowed only as our surrounding %…%)
 *   ,  — separator between or() clauses
 *   ()  — sub-expression grouping
 */
function sanitizePostgrestSearch(raw: string): string {
  return raw.replace(/[%,()]/g, '');
}

export function useProviderEntity(
  provider: ProviderDefinition | null | undefined,
  entity: EntityDefinition | null | undefined,
  params: EntityQueryParams = {},
) {
  const { sport, search, limit = 100, sortColumn, sortAscending } = params;
  // Compute the sanitized search value ONCE so the cache key and the
  // fetch see the same input. Previously the queryKey used the raw
  // `search` while the fetch used sanitizePostgrestSearch(search),
  // which created separate cache entries for distinct raw inputs that
  // collapsed to the same sanitized string (e.g. "alex," vs "alex").
  const safeSearch = search ? sanitizePostgrestSearch(search) : '';
  return useQuery({
    // `enabled` gates the fetch when either provider or entity is null
    // (route resolved to an unknown slug). Hooks at the call site still
    // run unconditionally per the Rules of Hooks; only the network call
    // is skipped.
    enabled: !!provider && !!entity,
    queryKey: ['entity', provider?.key, entity?.key, sport, safeSearch, limit, sortColumn, sortAscending],
    queryFn: async () => {
      if (!provider || !entity) {
        // The enabled gate above prevents this branch from ever running,
        // but TypeScript needs the narrow.
        return { rows: [] as Record<string, unknown>[], total: null as number | null };
      }
      const client = clientForSchema(provider.schema);
      // `estimated` (planner reltuples), not `exact`: an exact count scans the
      // whole table, which times out under the short `authenticated` role
      // statement budget on the large tables (e.g. genius_v2.persons ~360K) and
      // surfaces as a blank "—" count. An estimate is plenty for this browse view.
      let q = client.from(entity.table).select('*', { count: 'estimated' }).limit(limit);
      // Server-side sort drives the whole-table ordering. When the caller
      // supplies a column (clicked header) use it; otherwise fall back to the
      // entity's default `orderBy` so the initial view is unchanged.
      if (sortColumn) {
        q = q.order(sortColumn, { ascending: sortAscending ?? true });
      } else if (entity.orderBy) {
        q = q.order(entity.orderBy.column, { ascending: entity.orderBy.ascending });
      }
      const sportCol = entity.sportColumn ?? 'sport_key';
      if (sport) q = q.eq(sportCol, sport);
      if (safeSearch) {
        // Naive search across name-ish columns; PostgREST `or` syntax.
        const candidates = entity.columns
          .map((c) => c.key)
          .filter((k) => k.endsWith('_name') || k === 'first_name' || k === 'last_name' || k === 'display_name');
        if (candidates.length) {
          const expr = candidates.map((c) => `${c}.ilike.%${safeSearch}%`).join(',');
          q = q.or(expr);
        }
      }
      const { data, error, count } = await q;
      if (error) throw error;
      let total: number | null = count ?? null;
      if (!sport && !safeSearch) {
        // Unfiltered total: prefer the accurate n_live_tup count (the estimated
        // count is ~1/3 of reality under the table's is_admin_member() RLS qual).
        // For a filtered browse we keep the estimate (an exact filtered count can
        // time out on the large tables).
        const accurate = await fetchEntityRowCounts(provider.schema, [entity.table]).catch(
          () => new Map<string, number>(),
        );
        if (accurate.has(entity.table)) total = accurate.get(entity.table)!;
      }
      return { rows: (data ?? []) as Record<string, unknown>[], total };
    },
  });
}

export interface EntityStat {
  entity: string;
  label: string;
  count: number | null;
  error: string | null;
  /** ISO timestamp of the latest harvest (manifest collected_at), or null. */
  lastHarvested: string | null;
  /** ISO timestamp of the latest typed-table write (max updated_at), or null. */
  lastSynced: string | null;
}

/**
 * Per-entity row count + harvest/sync freshness for the provider landing page.
 * Each card shows the estimated row count, when the entity was last harvested
 * (manifest), and when its table was last written (synced).
 */
export function useEntityCounts(provider: ProviderDefinition | null | undefined) {
  return useQuery({
    // See note on useProviderEntity — `enabled` skips the network call
    // when the URL slug doesn't resolve to a known provider; the hook
    // itself still runs in the component to keep call order stable.
    enabled: !!provider,
    queryKey: ['entity-counts', provider?.key],
    queryFn: async (): Promise<EntityStat[]> => {
      if (!provider) return [];
      const client = clientForSchema(provider.schema);
      // One manifest read covers every card's "last harvested". If the view is
      // unavailable, degrade to an empty map (harvested → "—") rather than
      // failing the whole grid — the counts are still useful on their own.
      const harvestMap = await fetchManifestLastCollected(provider.key).catch(
        () => new Map<string, string>(),
      );
      // Accurate row counts in ONE call via harvest.fn_entity_row_counts (returns
      // pg_stat n_live_tup). PostgREST `count=estimated` returned the planner's
      // RLS-qual estimate, and the per-table RLS policy `is_admin_member()` is a
      // boolean FUNCTION the planner can't estimate → it applied a default 1/3
      // selectivity, so every card showed ~1/3 of the true count.
      const counts = await fetchEntityRowCounts(provider.schema, provider.entities.map((e) => e.table));
      return Promise.all(
        provider.entities.map(async (e) => {
          const lastSynced = await fetchLastSynced(client, e.table).catch(() => null);
          return {
            entity: e.key,
            label: e.label,
            count: counts.has(e.table) ? counts.get(e.table)! : (counts.size === 0 ? null : 0),
            error: null,
            lastHarvested: lastHarvestedFor(e, harvestMap),
            lastSynced,
          };
        }),
      );
    },
  });
}

/** Last-harvested + last-synced timestamps for a single entity (detail page). */
export function useEntityFreshness(
  provider: ProviderDefinition | null | undefined,
  entity: EntityDefinition | null | undefined,
) {
  return useQuery({
    enabled: !!provider && !!entity,
    queryKey: ['entity-freshness', provider?.key, entity?.key],
    staleTime: 60_000,
    queryFn: async (): Promise<{ lastHarvested: string | null; lastSynced: string | null }> => {
      if (!provider || !entity) return { lastHarvested: null, lastSynced: null };
      const client = clientForSchema(provider.schema);
      const [harvestMap, lastSynced] = await Promise.all([
        fetchManifestLastCollected(provider.key, harvestEndpointTypes(entity)).catch(
          () => new Map<string, string>(),
        ),
        fetchLastSynced(client, entity.table).catch(() => null),
      ]);
      return { lastHarvested: lastHarvestedFor(entity, harvestMap), lastSynced };
    },
  });
}
